import {html, LitElement, unsafeCSS} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';

// @ts-ignore
import styles from './route.css?inline'
import type {Grade, Route, Vote} from "../_types";
import {GradingSystem, RouteMeta} from "../_types";
import {findGradingSystem, getClosestGrade, getGradeDisplay} from "../lib/grading-system";
import {getMetaLabel} from "../lib/meta";
import {
    EVENT_ROUTE_EXPAND_TOGGLE,
    EVENT_ROUTE_UNVOTE,
    EVENT_ROUTE_VOTE,
    EventRouteExpandToggle,
    EventRouteUnvote,
    EventRouteVote
} from "./events";

export type i18n = {
    date?: string
    route_voting_label?: string
}

@customElement("climbpress-route")
export default class ClimbPressRoute extends LitElement {
    static styles = unsafeCSS(styles);

    @property({type: Object})
    public i18n: i18n = {}

    @property({type: Object})
    public route: Route | null = null

    @property({attribute: "meta-structure", type: Array})
    public metaStructure: RouteMeta[] = [];

    @property({attribute: "meta-collapsed", type: Array})
    public metaCollapsed?: string[]

    @property({attribute: "grading-systems", type: Object})
    public gradingSystems: GradingSystem[] = [];

    @property({attribute: "expanded", type: Boolean})
    public expanded: boolean = false

    @property({type: Object})
    public vote: Vote | null = null

    protected render() {
        const route = this.route;

        if (!route) return null;

        const grade = getGradeDisplay(route, this.gradingSystems);
        const metaKeys = Object.keys(route.metas);
        const metaKeysNotInCollapsedMetas = metaKeys.filter(key => {
            return !(this.metaCollapsed?.includes(key) ?? false);
        });

        const collapsedMetaValues = (this.metaCollapsed ?? metaKeys).map(key => {
            const value = route.metas[key] ?? "";
            return getMetaLabel(key, value, this.metaStructure) ?? value;
        }).filter(v => v != "");

        const classes = classMap({
            expanded: this.expanded,
        });

        const clickExpand = () => {
            if (!this.expanded) this.dispatchToggleExpand()
        }
        const clickCollapse = (e: Event) => {
            if (this.expanded) {
                e.preventDefault()
                e.stopPropagation()
                e.stopImmediatePropagation()
                this.dispatchToggleExpand()
            }
        }

        const gradingSystem = findGradingSystem(route, this.gradingSystems);
        const routeGrade = getClosestGrade(route, this.gradingSystems);

        const vote = this.vote;
        let votedGrade: { grade: Grade, index: number } | null = null;
        let nextHarderGrade: Grade | null = null;
        let nextEasierGrade: Grade | null = null;
        const minIndex = routeGrade ? routeGrade.index - 4 : 0;
        const maxIndex = routeGrade ? routeGrade.index + 4 : 0;

        if (routeGrade != null && gradingSystem) {
            if (vote) {
                votedGrade = getClosestGrade({
                    gradingSystem: route.gradingSystem,
                    difficulty: vote.difficulty,
                }, this.gradingSystems) ?? null;
            }
            const baseGrade = votedGrade ? votedGrade : routeGrade;
            const nextHarderGradeIndex = baseGrade.index + 1;
            const nextEasierGradeIndex = baseGrade.index -1;

            if(nextHarderGradeIndex <= maxIndex){
                nextHarderGrade = gradingSystem.grades?.[nextHarderGradeIndex] ?? null;
            }
            if(nextEasierGradeIndex >= minIndex){
                nextEasierGrade = gradingSystem.grades?.[nextEasierGradeIndex] ?? null;
            }
        }

        // TODO: move to box settings or global settings
        let communityVotingGrade: null | Grade = null
        if(route.communityVotesCount > 4){
            console.debug("community", route)
            communityVotingGrade = getClosestGrade({
                gradingSystem: route.gradingSystem,
                difficulty: route.communityAverageDifficulty,
            }, this.gradingSystems)?.grade ?? null;
        }
        const clickVote = (difficulty: number) => (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            if (difficulty < 0 || !routeGrade) {
                return;
            }

            const grade = getClosestGrade(
                {
                    gradingSystem: route?.gradingSystem,
                    difficulty,
                },
                this.gradingSystems
            );

            // TODO: make it block configuration or global setting max and min vote difference

            if (grade == null || grade.index < minIndex || grade.index > maxIndex) return;

            this.dispatchChangeVote(grade.grade.difficulty)
        }

        const clickSameVote = (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            if (route == null) return;
            if (vote === null) {
                this.dispatchChangeVote(route.difficulty);
            } else {
                this.dispatchChangeUnvote();
            }
        }

        const dateFormat = new Intl.DateTimeFormat(undefined,{
            dateStyle: "short",
        });

        const communityValues: string[] = []

        if(votedGrade){
            communityValues.push(votedGrade.grade.label);
        }
        if(communityVotingGrade){
            communityValues.push(`⌀ ${communityVotingGrade.label}`);
        }

        return html`
            <div class="route ${classes}" @click=${clickExpand}>
                <div class="details" @click=${clickCollapse}>
                    <div class="name" >${route.name}</div>
                    <div class="meta">
                        <div>
                            ${dateFormat.format(Date.parse(route.created))} •
                            ${collapsedMetaValues.join(" • ")}
                        </div>
                    </div>
                </div>
                <div class="difficulty">
                    <div class="value">
                        ${grade}
                        ${communityValues.length ?
                                html`<span class="my-value">${communityValues.join(" / ")}</span>`
                        :null}
                    </div>
                    
                    <div class="voting">
                        <div class="voting_wrapper">
                            <div class="label">${this.i18n.route_voting_label ?? "Deine Bewertung"}</div>
                            <div class="controls">
                                <button
                                        ?disabled=${nextEasierGrade == null}
                                        @click=${clickVote(nextEasierGrade?.difficulty ?? -1)}
                                        class=${classMap({
                                            "btn-easier": true,
                                            //active: votedGrade != null && currentVoteValue < route.difficulty
                                        })}
                                >
                                    –
                                </button>
                                <button
                                        @click=${clickSameVote}
                                        class=${classMap({active: vote != null})}
                                >
                                    ${votedGrade ? votedGrade.grade.label : grade}
                                </button>
                                <button
                                        ?disabled=${nextHarderGrade == null}
                                        @click=${clickVote(nextHarderGrade?.difficulty ?? -1)}
                                        class=${classMap({
                                            "btn-harder": true,
                                            //active: votedGrade != null && currentVoteValue > route.difficulty
                                        })}
                                >
                                    +
                                </button>
                            </div>
                            <div class="community-voting">
                                <div class="label">⌀ Bewertung</div>
                                <div>${communityVotingGrade?.label ?? "-" }</div>
                            </div>
                           
                        </div>                        
                    </div>
                </div>
                <div class="expanded-details">
                    <div>
                        <dl>
                            <dt>${this.i18n.date ?? "Datum"}</dt>
                            <dd>${dateFormat.format(Date.parse(route.created))}</dd>
                            ${this.metaCollapsed?.map(this.renderRouteMeta.bind(this, route))}
                            ${metaKeysNotInCollapsedMetas.map(this.renderRouteMeta.bind(this, route))}
                        </dl>
                    </div>
                </div>
            </div>
        `
    }

    private renderRouteMeta(route: Route, metaKey: string) {
        const value = route.metas[metaKey] ?? "";
        if (value == "") return '';
        const structure = this.metaStructure.find(m => m.key == metaKey)
        const label = structure?.label ?? metaKey;
        const display = structure?.options?.find(o => o.key == value)?.label ?? value;
        return html`
            <dt>${label}</dt>
            <dd>${display}</dd>
        `
    }

    // ------------------------------------------------
    // Events
    // ------------------------------------------------
    private dispatchChangeVote(value: number) {
        const routeId = this.route?.id;
        if (!routeId) return;
        this.dispatchEvent(
            new CustomEvent<EventRouteVote>(
                EVENT_ROUTE_VOTE,
                {
                    detail: {
                        difficulty: value,
                        route: routeId,
                    },
                    bubbles: true,
                    composed: true
                },
            )
        )
    }

    private dispatchChangeUnvote() {
        const routeId = this.route?.id;
        if (!routeId) return;
        this.dispatchEvent(
            new CustomEvent<EventRouteUnvote>(
                EVENT_ROUTE_UNVOTE,
                {
                    detail: {
                        route: routeId,
                    },
                    bubbles: true,
                    composed: true,
                },
            )
        )
    }

    private dispatchToggleExpand() {
        const routeId = this.route?.id;
        if (!routeId) return;
        this.dispatchEvent(
            new CustomEvent<EventRouteExpandToggle>(
                EVENT_ROUTE_EXPAND_TOGGLE,
                {
                    detail: {
                        route: routeId,
                    },
                    bubbles: true,
                    composed: true,
                },
            )
        )
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'climbpress-route': ClimbPressRoute
    }
}
